import React, { useEffect, useRef, useState } from "react";
import AppBreadcrumb from "../../components/CustomeBreadcrumb/AppBreadcrumb";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import { getAllDetails, postAllData, postSingleData } from "../../service/ProductService";
import { CHANGE_DEFAULT_PAYMENT_CARD, GET_PROFILE_DATA_BY_ID, UPDATE_CLIENT_DATA } from "../../config/ApiConstant";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { validateClientProfileData } from "../../config/Validate";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import moment from "moment";
import { Checkbox } from "primereact/checkbox";
import { Badge } from "primereact/badge";
import { provinces, SkeletonbodyTemplate, Skeletonitems } from "../../appconfig/Settings";
import UpdatePaymentMethod from "../../components/BankDetails/BankDetails";
import { formatDate } from "../../appconfig/AppHelper";

const ClientProfile = () => {
  // get userData from local storage
  const rawUserData = localStorage.getItem("user_data");
  let userData: any = null;
  userData = rawUserData ? JSON.parse(rawUserData) : null;
  const userRole = userData?.roles?.role_name || "";

  //   navigate
  const navigate = useNavigate();

  //   toast ref
  const toast = useRef<any>(null);

  // error state
  const [errors, setErrors] = useState<any>({});

  // loading
  const [pageLoading, setPageLoading] = useState<any>(true);

  // btn submit loading
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  // profile data obj
  const [profileData, setProfileData] = useState<any>();

  //   add edit profile dialog
  const [editProfileDialog, setEditProfileDialog] = useState<boolean>(false);

  // state for business number to display static
  const [displayBusinessNumber, setDisplayBusinessNumber] = useState("");

  // stripe card detail form model
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);

  // card details
  const [cardDetailsList, setCardDetailsList] = useState<any>([]);

  // states to change default payment card
  const [showDefaultModal, setShowDefaultModal] = useState<boolean>(false);
  const [selectedCard, setSelectedCard] = useState<any>(null);
  const [cardSubmitLoading, setCardSubmitLoading] = useState<boolean>(false);

  // setting business number for display purpose static
  useEffect(() => {
    if (profileData?.bussiness_number) {
      const val = profileData?.bussiness_number;
      const firstNine = val?.slice(0, 9);
      const rest = val?.slice(9);
      const displayVal = val?.length > 9 ? `${firstNine} RP ${rest}` : val;
      setDisplayBusinessNumber(displayVal);
    }
  }, [profileData?.bussiness_number]);

  // get profile data
  useEffect(() => {
    getProfileData();
  }, []);

  // get profile data
  const getProfileData = async () => {
    try {
      setPageLoading(true);
      const response = await getAllDetails(`${GET_PROFILE_DATA_BY_ID}/${userData?.id}`);

      if (response) {
        setCardDetailsList(response?.card_details);
        // set local storage with card detail added or not (flag)
        const payment_details_added = response?.payment_details_added;
        localStorage.setItem("payment_details_added", payment_details_added);
        setProfileData(response);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message,
      });
    } finally {
      setPageLoading(false);
    }
  };

  // onchange
  const onInputChange = (e: any, name: string) => {
    let val;
    if (name === "vacation_hold" || name === "custom_bi_weekly") {
      val = e.checked ? true : false;
    } else if (name === "company_logo" && e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
      const maxSize = 2 * 1024 * 1024; // 2MB

      if (!allowedTypes.includes(file.type)) {
        setErrors({ ...errors, company_logo: "Only JPG, JPEG, and PNG files are allowed" });
        return;
      }

      if (file.size > maxSize) {
        setErrors({ ...errors, company_logo: "File size must be less than 2MB" });
        return;
      }
      val = file;
    } else {
      val = e?.target?.value ?? e?.value;
    }

    // format postal code: add space after 3 characters
    if (name === "postal_code" && val) {
      // remove all spaces first
      val = val.replace(/\s+/g, "").toUpperCase();
      // insert space after 3 characters if length > 3
      if (val.length > 3) {
        val = val.slice(0, 3) + " " + val.slice(3);
      }
    }

    // format Business number
    if (name === "bussiness_number") {
      // remove spaces and RP
      const cleanVal = val.replace(/\s+/g, "").replace("RP", "");
      // const cleanVal = val;

      // create display version
      let displayVal = cleanVal;
      if (cleanVal.length > 9) {
        const firstNine = cleanVal.slice(0, 9);
        const rest = cleanVal.slice(9);
        displayVal = `${firstNine} RP ${rest}`;
      }

      // update what user sees in input
      setDisplayBusinessNumber(displayVal);

      // update your global state with clean (no RP)
      setProfileData({
        ...profileData,
        bussiness_number: cleanVal,
      });
      setErrors({ ...errors, [name]: "" });

      return;
    }

    setProfileData({ ...profileData, [name]: val });
    setErrors({ ...errors, [name]: "" });
  };

  // api call to change default payment card
  const handleMakeDefault = async () => {
    try {
      setCardSubmitLoading(true);
      const response = await postSingleData(`${CHANGE_DEFAULT_PAYMENT_CARD}/${selectedCard?.id}`);
      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        setShowDefaultModal(false);
        getProfileData();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setCardSubmitLoading(false);
    }
  };

  // update api call of profile data for userRole === client
  const submitProfileData = async () => {
    try {
      const { isError, errors: validationErrors } = validateClientProfileData(profileData);

      if (isError || errors.company_logo) {
        setErrors({ ...validationErrors, company_logo: errors.company_logo });
        return;
      }

      setSubmitLoading(true);
      const formData = new FormData();
      formData.append("company_name", profileData.company_name);
      formData.append("email", profileData.email);
      formData.append("phone", profileData.phone);
      formData.append("bussiness_number", profileData.bussiness_number);
      formData.append("gst_no", profileData.gst_no || "");
      formData.append("pst_no", profileData.pst_no || "");
      formData.append("address", profileData.address);
      formData.append("postal_code", profileData?.postal_code);
      formData.append("province", profileData?.province);
      formData.append("city", profileData?.city);
      formData.append("edit", "profile");
      formData.append("type", "Client");

      if (profileData.company_logo instanceof File) {
        formData.append("company_logo", profileData.company_logo);
      }

      const response = await postAllData(UPDATE_CLIENT_DATA + "/" + profileData.id, formData);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        hideEditDialog();

        // object to update local storage
        // object to update local storage
        const updatedData = {
          ...userData,
          company_name: profileData.company_name,
          company_logo: response?.data?.company_logo || userData?.company_logo,
          email: profileData.email,
          phone: profileData.phone,
          bussiness_number: profileData.bussiness_number,
          gst_no: profileData.gst_no || userData?.gst_no,
          //   client_charge: profileData.client_charge,
          address: profileData.address,
          postal_code: profileData?.postal_code,
          province: profileData?.province,
          city: profileData?.city,
        };
        // update local storage
        localStorage.setItem("user_data", JSON.stringify(updatedData));

        // Refresh the page as requested
        window.location.reload();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  // profile edit dialog
  const editDialog = () => {
    setEditProfileDialog(true);
    // Ensure we start the edit with the most recent data available in state
    if (profileData) {
      setProfileData({
        ...profileData
      });
    } else {
      setProfileData(userData);
    }
  };

  // hide profile dialog
  const hideEditDialog = () => {
    setEditProfileDialog(false);
    setErrors({});
  };

  // card number template
  const cardNumberTemplate = (rowData: any) => {
    return <div className="">{`********${rowData?.last4}`}</div>;
  };

  // month and year expire date
  const expiryTemplate = (rowData: any) => {
    const date = moment(`${rowData?.exp_year}-${rowData?.exp_month}-01`, "YYYY-M-D");

    return <span>{date.format("MMM, YYYY")}</span>;
  };

  // on default card change
  const onDefaultCheckboxClick = (rowData: any) => {
    if (rowData.is_default) return;

    setSelectedCard(rowData);
    setShowDefaultModal(true);
  };

  // default selected card template
  const defaultSelectedCard = (rowData: any) => {
    return (
      <>
        <Checkbox checked={rowData.is_default} onChange={() => onDefaultCheckboxClick(rowData)} className="mr-2" />

        {rowData.is_default && <Badge value="Default" severity="success" />}
      </>
    );
  };

  return (
    <>
      <AppBreadcrumb
        pageName="Profile"
        currentMenu="Admin"
        pageTitle={
          <>
            <div className="gotopage-button-header">
              <Button icon="pi pi-arrow-left" className="mr-2 common-p-gotopage-button" onClick={() => navigate("/client-dashboard")} />
              <span className="span-text">Profile</span>
            </div>
          </>
        }
      />
      <Card
        className="p-0 bg-white"
        footer={
          <>
            <div className="flex align-items-end justify-content-end align-self-end align-content-end">
              <Button label="Edit" icon="pi pi-pencil" onClick={() => editDialog()} />
            </div>
          </>
        }
      >
        <Toast ref={toast} />
        <div className="grid">
          {/* name */}
          <div className="flex gap-2 align-items-center mb-3 col-12">
            <div className="flex gap-2 align-items-center">
              {profileData?.company_logo ? (
                <img
                  src={typeof profileData.company_logo === 'string' ? profileData.company_logo : userData?.company_logo}
                  alt="Company Logo"
                  style={{ width: "130px", height: "130px", borderRadius: "50%", objectFit: "contain", border: "1px solid #dee2e6" }}
                />
              ) : (
                <div style={{ width: "130px", height: "130px", borderRadius: "50%", backgroundColor: "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dee2e6" }}>
                  <i className="pi pi-user" style={{ fontSize: "2rem", color: "#999" }}></i>
                </div>
              )}
            </div>
            <div className="">
              <label className="font-bold text-gray-700">Company Name :</label>
              <div className="flex gap-2 align-items-center">
                <h2 className="mt-1 mb-1 text-xl font-semibold text-gray-900">{profileData?.company_name ?? ""}</h2>
              </div>
            </div>
          </div>
          {/* other details */}
          <div className="col-12 md:col-12">
            <div className="grid p-fluid">
              {/* email */}
              {!window.cn(userData?.email) && (
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label className="font-bold text-gray-700">Email:</label>
                  <div className="text-gray-900">{userData?.email || "-"}</div>
                </div>
              )}
              {/* phone */}
              {!window.cn(userData?.phone) && (
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label className="font-bold text-gray-700">Phone:</label>
                  <div className="text-gray-900">{userData?.phone || "-"}</div>
                </div>
              )}

              {/* website url */}
              <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                <label style={{ fontWeight: "bold" }}>Website Url:</label>
                <div>
                  {userData?.website_url ? (
                    <a style={{ color: "blue", textDecoration: "underline" }} href={userData?.website_url} target="_blank" rel="noopener noreferrer">
                      {userData?.website_url}
                    </a>
                  ) : (
                    "-"
                  )}
                </div>
              </div>

              {/* client payment */}
              {!window.cn(userData?.client_charge) && (
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label className="font-bold text-gray-700">Client Payment:</label>
                  <div className="text-gray-900">{userData?.client_charge || "-"}</div>
                </div>
              )}

              {/* business no */}
              {!window.cn(userData?.bussiness_number) && (
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Business No:</label>
                  <div>{userData?.bussiness_number?.slice(0, 9) + " RP " + userData?.bussiness_number?.slice(-4) || "-"}</div>
                </div>
              )}

              {/* pay schema */}
              {!window.cn(userData?.pay_schema) && (
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label className="font-bold text-gray-700">Pay Scheme:</label>
                  <div className="text-gray-900">{userData?.pay_schema || "-"}</div>
                </div>
              )}

              {/* bi weekly cycle start date */}
              {userData?.custom_bi_weekly === true && userData?.pay_schema === "Bi Weekly" && (
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Bi Weekly Cycle Start Date:</label>
                  <div>{userData?.bi_weekly_start_date ? `${formatDate(userData?.bi_weekly_start_date)}` : "-"}</div>
                </div>
              )}

              {/* pay check days */}
              {!window.cn(userData?.pay_schema) && (
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label className="font-bold text-gray-700">Pay Cheque Day:</label>
                  <div>
                    {" "}
                    {userData?.pay_schema === "Monthly" || userData?.pay_schema === "Semi Monthly"
                      ? userData?.paycheck
                      : userData?.pay_schema === "Bi Weekly"
                        ? "Upcoming Friday"
                        : userData?.paycheck || "-"}
                  </div>
                </div>
              )}

              {/* Address */}
              {!window.cn(userData?.address) && (
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label className="font-bold text-gray-700">Address:</label>
                  <div className="text-gray-900">{userData?.address || "-"}</div>
                </div>
              )}

              {/* postal code */}
              {!window.cn(userData?.postal_code) && (
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label className="font-bold text-gray-700">Postal Code:</label>
                  <div className="text-gray-900">{userData?.postal_code || "-"}</div>
                </div>
              )}

              {/* gst no */}
              <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                <label className="font-bold text-gray-700">GST Number:</label>
                <div className="text-gray-900">{userData?.gst_no || "-"}</div>
              </div>

              {/* pst no */}
              <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                <label className="font-bold text-gray-700">PST Number:</label>
                <div className="text-gray-900">{userData?.pst_no || "-"}</div>
              </div>


              {/* province */}
              {!window.cn(userData?.province) && (
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label className="font-bold text-gray-700">Province:</label>
                  <div className="text-gray-900">{userData?.province || "-"}</div>
                </div>
              )}

              {/* city */}
              {!window.cn(userData?.city) && (
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label className="font-bold text-gray-700">City:</label>
                  <div className="text-gray-900">{userData?.city || "-"}</div>
                </div>
              )}


            </div>
          </div>
        </div>
      </Card>

      {/* card details table */}
      {/* <div className="grid mt-5">
        <div className="col-12 md:col-8">
          <Card className=" bg-white ">
            <div className="card-header mb-3 flex justify-content-between">
              <h5 className="font-bold text-gray-700 mb-0">Card Details</h5>
              <Button
                label="Add Card"
                icon="pi pi-plus"
                className="common-primary-button"
                onClick={() => setShowPaymentModal(true)}
              />
            </div>
            {!pageLoading ? (
              <DataTable
                className="p-datatable-customers"
                value={cardDetailsList}
                dataKey="id"
                emptyMessage="No Card Details Found"
                rowHover
              >
                <Column
                  field="brand"
                  header="Brand"
                  style={{ minWidth: "14rem" }}
                ></Column>
                <Column
                  field="last4"
                  header="Card No"
                  style={{ minWidth: "10rem" }}
                  body={cardNumberTemplate}
                ></Column>

                <Column
                  header="Expiry"
                  body={expiryTemplate}
                  style={{ minWidth: "10rem" }}
                />
                <Column
                  field="is_default"
                  header="Default Card"
                  body={defaultSelectedCard}
                ></Column>
              </DataTable>
            ) : (
              <DataTable value={Skeletonitems}>
                <Column
                  field="brand"
                  header="Brand"
                  style={{ minWidth: "14rem" }}
                  body={SkeletonbodyTemplate}
                />
                <Column
                  field="last4"
                  header="Card No"
                  style={{ minWidth: "10rem" }}
                  body={SkeletonbodyTemplate}
                />
                <Column
                  header="Expiry"
                  style={{ minWidth: "10rem" }}
                  body={SkeletonbodyTemplate}
                />
                <Column
                  field="is_default"
                  header="Default Card"
                  body={SkeletonbodyTemplate}
                />
              </DataTable>
            )}
          </Card>
        </div>
      </div> */}

      {/* Add/Edit payee Dialog */}
      <Dialog
        visible={editProfileDialog}
        className="mx-3 lg:mx-0"
        style={{ width: "800px" }}
        header={"Edit Profile"}
        draggable={false}
        modal
        footer={
          <div className="flex pt-2 align-items-center justify-content-end">
            <Button label="Cancel" icon="pi pi-times" className="mr-2 p-button-secondary" onClick={hideEditDialog} />
            <Button label="Submit" icon="pi pi-check" onClick={submitProfileData} className="common-primary-button" loading={submitLoading} />
          </div>
        }
        onHide={hideEditDialog}
      >
        <div className="grid p-fluid formgrid">
          {/* Company Name */}
          <div className="field col-12 md:col-6">
            <label htmlFor="company_name">Company Name *</label>
            <div className="input-effect-div">
              <InputText
                value={profileData?.company_name || ""}
                name="company_name"
                autoComplete="off"
                placeholder="Enter Company Name"
                onChange={(e) => onInputChange(e, "company_name")}
                className={errors["company_name"] && "p-invalid"}
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["company_name"]}</small>
          </div>
          <div className="field col-12 md:col-6">
            <label htmlFor="company_logo">Company Logo</label>
            <div className="input-effect-div" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <InputText
                type="file"
                name="company_logo"
                autoComplete="off"
                onChange={(e) => onInputChange(e, "company_logo")}
                className={errors["company_logo"] && "p-invalid"}
                style={{ color: profileData?.company_logo && typeof profileData.company_logo === 'string' ? 'transparent' : 'inherit' }}
                accept="image/*"
              />
              {profileData?.company_logo && typeof profileData.company_logo === 'string' && (
                <div style={{
                  position: 'absolute',
                  left: '110px', // Adjusted to start after the "Choose File" button
                  pointerEvents: 'none',
                  color: '#495057',
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 'calc(100% - 120px)'
                }}>
                  {profileData.company_logo.split('/').pop()}
                </div>
              )}
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["company_logo"]}</small>
            <div className="mt-1" style={{ fontSize: '12px', color: 'red' }}>
              Please upload a transparent logo (Max size: 2MB).
            </div>
          </div>

          {/* Email */}
          <div className="field col-12 md:col-6">
            <label htmlFor="email">Email *</label>
            <div className="input-effect-div">
              <InputText
                value={profileData?.email || ""}
                name="email"
                autoComplete="off"
                placeholder="Enter Email"
                onChange={(e) => onInputChange(e, "email")}
                className={errors["email"] && "p-invalid"}
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["email"]}</small>
          </div>
          {/* Phone */}
          <div className="field col-12 md:col-6">
            <label htmlFor="phone">Phone *</label>
            <div className="flex-1 input-effect-div p-inputgroup">
              <span className="font-bold p-inputgroup-addon">+1</span>
              <InputText
                value={profileData?.phone || ""}
                name="phone"
                autoComplete="off"
                placeholder="Enter Phone Number"
                onChange={(e) => onInputChange(e, "phone")}
                className={errors["phone"] && "p-invalid"}
                keyfilter="pint"
                maxLength={10}
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["phone"]}</small>
          </div>

          {/* GST Number */}
          <div className="field col-12 md:col-6">
            <label htmlFor="gst_no">GST Number </label>
            <div className="input-effect-div">
              <InputText
                value={profileData?.gst_no || ""}
                name="gst_no"
                autoComplete="off"
                placeholder="Enter GST Number"
                onChange={(e) => onInputChange(e, "gst_no")}
                className={errors["gst_no"] && "p-invalid"}
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["gst_no"]}</small>
          </div>

          {/* PST Number */}
          <div className="field col-12 md:col-6">
            <label htmlFor="pst_no">PST Number</label>
            <div className="input-effect-div">
              <InputText
                value={profileData?.pst_no || ""}
                name="pst_no"
                autoComplete="off"
                placeholder="Enter PST Number"
                onChange={(e) => onInputChange(e, "pst_no")}
              />
              <span className="focus-bg"></span>
            </div>
          </div>

          {/* client Payment */}
          {/* <div className="field col-12 md:col-6">
            <label htmlFor="client_charge">Client Payment *</label>
            <form autoComplete="off">
              <div className="flex-1 input-effect-div p-inputgroup">
                <span className="font-bold p-inputgroup-addon">$</span>
                <InputNumber
                  value={profileData?.client_charge || ""}
                  onChange={(e: any) => onInputChange(e, "client_charge")}
                  placeholder="Enter Client Payment"
                  name="client_charge"
                  className={errors["client_charge"] && "p-invalid"}
                  // minFractionDigits={2}
                  maxFractionDigits={2}
                  mode="decimal"
                  useGrouping={false}
                />
              </div>
            </form>

            <small className="p-invalid-txt">{errors["client_charge"]}</small>
          </div> */}

          {/* Postal Code */}
          <div className="field col-12 md:col-6">
            <label htmlFor="postal_code">Postal Code *</label>
            <div className="input-effect-div">
              <InputText
                value={profileData?.postal_code || ""}
                name="postal_code"
                placeholder="Enter Postal Code"
                autoComplete="off"
                onChange={(e) => onInputChange(e, "postal_code")}
                className={errors["postal_code"] && "p-invalid"}
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["postal_code"]}</small>
          </div>

          <div className="field md:col-6 col-12">
            <label htmlFor="province">Select Province *</label>
            <Dropdown
              value={profileData?.province || ""}
              onChange={(e) => onInputChange(e, "province")}
              options={provinces}
              placeholder="Select Province"
              className={errors["province"] ? "p-invalid" : ""}
            />
            <small className="p-invalid-txt">{errors["province"]}</small>
          </div>

          {/* city */}
          <div className="field col-12 md:col-6">
            <label htmlFor="name">City *</label>
            <div className="input-effect-div">
              <InputText
                value={profileData?.city || ""}
                name="city"
                placeholder="Enter City"
                autoComplete="off"
                onChange={(e) => onInputChange(e, "city")}
                className={errors["city"] && "p-invalid"}
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["city"]}</small>
          </div>

          {/* Address */}

          <div className="field col-12 md:col-6">
            <label htmlFor="address">Address *</label>
            <div className="input-effect-div">
              <InputTextarea
                rows={3}
                value={profileData?.address || ""}
                name="address"
                autoComplete="off"
                placeholder="Enter Address"
                onChange={(e) => onInputChange(e, "address")}
                className={errors["address"] && "p-invalid"}
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["address"]}</small>
          </div>
        </div>
      </Dialog>

      {/* dialog for stripe card detail form */}
      <Dialog
        header="Add Card Details"
        visible={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        modal
        draggable={false}
        dismissableMask={false}
        style={{ width: "35rem" }}
        className="mx-3 lg:mx-0"
        footer={<div></div>}
      >
        <UpdatePaymentMethod customerId={userData?.customer_id} onSuccess={() => setShowPaymentModal(false)} getApiCall={() => getProfileData()} />
      </Dialog>

      {/* dialog to change default payment card */}
      <Dialog
        header="Make Default Card"
        visible={showDefaultModal}
        style={{ width: "400px" }}
        className="mx-3 lg:mx-0"
        draggable={false}
        onHide={() => setShowDefaultModal(false)}
        footer={
          <>
            <div className="flex justify-content-end gap-2">
              <Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={() => setShowDefaultModal(false)} />
              <Button
                label="Confirm"
                icon="pi pi-check"
                loading={cardSubmitLoading}
                className="common-primary-button"
                onClick={() => handleMakeDefault()}
              />
            </div>
          </>
        }
      >
        <h6>Are you sure you want to make this card as your default payment card?</h6>
      </Dialog>
    </>
  );
};

export default ClientProfile;
